import { Injectable, inject } from '@angular/core';
import { IFilterService, IQService } from 'angular';
import { ApiService } from '../../api/api.service';
import { ValidateService } from '../../validate/validate.service';
import { FILTER_PORT, Q_PORT } from '../../utils';
import { AjsResponse } from '../../api/ajs-response';
import { TODO } from 'xstate';

/**
 * CartCouponsService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/cart/coupons/coupons.service.ts
 * Nested sub-service of CartService. Manages coupon codes in the shopping cart:
 * validation, filtering, and state tracking of valid/invalid/conflicting coupons.
 *
 * Registered via downgradeInjectable in ng-downgraded-components.ts as 'CartCouponsService'.
 * Wave 4 — Cart sub-services.
 */

export const COUPON_CODE_STATUS = {
  VALID_COUPON_CODE: 1,
  INVALID_COUPON_CODE: 0,
  CONFLICTING_COUPON_CODE: -1,
} as const;

export type CouponCodeStatus = typeof COUPON_CODE_STATUS;

export type CartCouponsParent = {
  model: { id: string; couponCodes: string[] };
  updateCart: () => Promise<TODO>;
};

@Injectable({ providedIn: 'root' })
export class CartCouponsService {
  private $q = inject<IQService>(Q_PORT);
  private $filter = inject<IFilterService>(FILTER_PORT);
  private apiService = inject(ApiService);
  private validateService = inject(ValidateService);

  readonly codes = COUPON_CODE_STATUS;

  parent = {} as CartCouponsParent;
  model: { code: string; valid?: number }[] = [];
  valid = false;
  validCouponCodes: TODO[] = [];
  invalidCouponCodes: TODO[] = [];
  conflictingCouponCodes: TODO[] = [];
  events: Record<string, TODO> = {};
  errors: Record<string, TODO> | false = false;

  init(parent: CartCouponsParent): this {
    this.parent = parent;
    return this;
  }

  setModel(data: { code: string; valid?: number }[]): void {
    this.model = data;
  }

  getModel(): { code: string; valid?: number }[] {
    return this.model;
  }

  resetModel(): void {
    this.model = [];
    this.validCouponCodes = [];
    this.invalidCouponCodes = [];
    this.conflictingCouponCodes = [];
  }

  getModelCodes(): string[] {
    const result: string[] = [];
    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i] && this.model[i].code) {
        result.push(this.model[i].code);
      }
    }
    return result;
  }

  update(): Promise<TODO> {
    this.parent.model.couponCodes = this.getModelCodes();
    return this.parent.updateCart();
  }

  findValid(code: string): TODO[] {
    return code ? (this.$filter('filter') as TODO)(this.validCouponCodes, { couponCode: code }) : [];
  }

  isValid(code: string): number | false {
    return this.findValid(code).length || false;
  }

  isInvalid(code: string): boolean {
    return this.invalidCouponCodes.indexOf(code) > -1;
  }

  isConflicting(code: string): boolean {
    return this.conflictingCouponCodes.indexOf(code) > -1;
  }

  checkCodes(): void {
    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.isInvalid(this.model[i].code)) {
        this.model[i].valid = this.codes.INVALID_COUPON_CODE;
      } else if (this.isConflicting(this.model[i].code)) {
        this.model[i].valid = this.codes.CONFLICTING_COUPON_CODE;
      } else if (this.isValid(this.model[i].code)) {
        this.model[i].valid = this.codes.VALID_COUPON_CODE;
      }
    }
  }

  findMembershipCoupon(membershipInfo: TODO): TODO[] {
    return (this.$filter('filter') as TODO)(this.validCouponCodes, (v1: TODO) => {
      const result =
        (this.$filter('filter') as TODO)(v1.membershipOfferingInfo, {
          levelId: membershipInfo.membershipCategoryId,
          offeringId: membershipInfo.membershipOfferingId,
        }) || [];
      return result.length ? v1 : false;
    });
  }

  findEventTicketCoupon(selectedEventId: string, ticketTypeId: string): TODO[] {
    let eventId: string;
    const result: TODO[] = [];

    for (let i = 0, ii = this.validCouponCodes.length; i < ii; i++) {
      const coupon = this.validCouponCodes[i];
      if (!selectedEventId) {
        continue;
      }
      for (eventId in coupon.eventPricepointMap) {
        const eventPricepoints = coupon.eventPricepointMap[eventId] || [];
        if (eventId === selectedEventId) {
          const found = { eventId: selectedEventId, couponCode: coupon.couponCode };
          if (ticketTypeId && eventPricepoints.indexOf(ticketTypeId) > -1) {
            result.push(found);
          }
          break;
        }
      }
    }

    return result;
  }

  validate(): Promise<TODO> {
    const q = this.$q.defer();
    const couponCodes = this.getModelCodes();

    this.apiService.cart.validateCoupons({ shoppingCartId: this.parent.model.id, couponCodes }).then(
      (response: AjsResponse<TODO>) => {
        this.validCouponCodes = response.data.payload.validCouponcodes || [];
        this.invalidCouponCodes = response.data.payload.invalidCouponCodes || [];
        this.conflictingCouponCodes = response.data.payload.conflictingCoupons || [];
        this.checkCodes();
        this.process(response.data.payload);
        q.resolve(response);
      },
      (response) => {
        q.reject(response);
      },
    );

    return q.promise as Promise<TODO>;
  }

  validateCoupons(couponCodes: string[]): boolean {
    let result = true;
    const validators: TODO[] = [];

    if (Array.isArray(couponCodes) && couponCodes.length) {
      for (let i = 0, ii = couponCodes.length; i < ii; i++) {
        const couponCode = couponCodes[i];
        validators.push({ field: 'form-coupon-' + i, coupon: { message: 'Invalid code', test: { code: couponCode } } });
        result = this.validateService.validate(validators);
      }
      if (result) {
        this.clearErrors();
      } else {
        this.errors = this.validateService.getErrorHash();
      }
    } else {
      this.clearErrors();
    }

    return result;
  }

  clearErrors(): void {
    this.errors = false;
  }

  setValid(valid: boolean): void {
    this.valid = valid;
  }

  addCoupon(code: string): void {
    const emptyCodes = this.model.filter((coupon) => coupon.code === '');
    if (!emptyCodes.length) {
      this.model.push({ code: code || '' });
    }
  }

  findCoupon(code: string): { index: number; coupon: TODO } | false {
    let found: { index: number; coupon: TODO } | false = false;
    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].code === code) {
        found = { index: i, coupon: this.model[i] };
      }
    }
    return found;
  }

  process(data: TODO): void {
    const coupons = data.validCouponcodes || [];
    const invalidCoupons = data.invalidCouponCodes || [];
    const conflictingCoupons = data.conflictingCoupons || [];
    const events: Record<string, TODO> = {};
    let found: ReturnType<typeof this.findCoupon>;

    this.setValid(data.valid);

    for (let i = 0, ii = coupons.length; i < ii; i++) {
      found = this.findCoupon(coupons[i].couponCode);
      if (!found) {
        this.addCoupon(coupons[i].couponCode);
        found = this.findCoupon(coupons[i].couponCode);
      }
      if (found) {
        events[coupons[i].couponCode] = Array.isArray(coupons[i].events) ? coupons[i].events : [];
      }
    }
    this.events = events;
    this.validCouponCodes = coupons;

    this.invalidCouponCodes = invalidCoupons;
    for (let i = 0, ii = this.invalidCouponCodes.length; i < ii; i++) {
      found = this.findCoupon(this.invalidCouponCodes[i]);
      if (found) {
        if (!this.errors) {
          this.errors = {};
        }
        (this.errors as Record<string, TODO>)['form-coupon-' + found.index] = { message: 'Invalid coupon' };
      }
    }

    this.conflictingCouponCodes = conflictingCoupons || [];
    for (let i = 0, ii = conflictingCoupons.length; i < ii; i++) {
      for (let j = 1, jj = this.conflictingCouponCodes[i].conflictingCouponCodes.length; j < jj; j++) {
        found = this.findCoupon(this.conflictingCouponCodes[i].conflictingCouponCodes[j]);
        if (found) {
          if (!this.errors) {
            this.errors = {};
          }
          (this.errors as Record<string, TODO>)['form-coupon-' + found.index] = {
            message: 'Coupon conflicts with: ' + this.conflictingCouponCodes[i].conflictingCouponCodes[0],
          };
        }
      }
    }
  }
}
